import React, { useCallback, useEffect, useRef, useState } from 'react';

import { IntlProvider } from 'react-intl';

import ThemeProvider from '@sumologic/yama-ds-components/themes/ThemeProvider';
import { lightTheme, darkTheme } from '@sumologic/yama-ds-components/themes';
import { sumoFetch } from '@sbu/utils.core-utils';

import { localizations } from '~/modules/lang';

import Login from '~/containers/Login/Login';

import CanManager from '~/containers/Can/CanManager';

import { AppRouter } from '~/containers/AppRouter/AppRouter';

import NoAuthenticationRouter, {
    noAuthPagePathsValues
} from '~/containers/NoAuthenticationRouter/NoAuthenticationRouter';

import ErrorBoundary from '~/containers/ErrorBoundary/ErrorBoundary';

import StickyAlertManager from '~/containers/StickyAlertManager/StickyAlertManager';

import { FeatureFlagManager } from '~/containers/FeatureFlagManager';
import { isDevEnv } from '~/modules/helpers/environment';
import { checkFeatureFlag } from '~/modules/helpers';

import AppContext from './AppContext';

const LS_THEME_KEY = SETTINGS.THEME_KEY;

const App = () => {
    const win = document.defaultView;
    const location = win.location;
    const uri = location.pathname;
    const isPageWithoutLogin = noAuthPagePathsValues.some(noAuthPath =>
        uri.includes(noAuthPath)
    );
    const langCode = win.CURRENT_USER.lang || win.LANG.systemLang;
    const localizedMessages = localizations?.[langCode] || {};
    const ref = useRef({}).current;

    // Initialize theme from localStorage for instant render, backend sync happens after mount
    const [theme, setTheme] = useState(() => {
        const savedTheme = localStorage.getItem(LS_THEME_KEY);
        return savedTheme === 'light' ? lightTheme : darkTheme;
    });

    const themeSwapRef = useRef(null);

    const applyTheme = useCallback(newTheme => {
        if (themeSwapRef.current) {
            themeSwapRef.current.newLink.remove();
            themeSwapRef.current = null;
        }

        const oldLink = document.getElementById('theme-styles');
        const newHref =
            newTheme === 'light'
                ? SETTINGS.light_theme_path
                : SETTINGS.dark_theme_path;
        const newThemeObj = newTheme === 'light' ? lightTheme : darkTheme;

        const newLink = document.createElement('link');
        newLink.rel = 'stylesheet';
        newLink.href = newHref;
        newLink.id = 'theme-styles-new';

        newLink.addEventListener('load', () => {
            setTheme(newThemeObj);
            if (oldLink && oldLink.parentNode) {
                oldLink.remove();
            }
            newLink.id = 'theme-styles';
            themeSwapRef.current = null;
        }, { once: true });

        newLink.addEventListener('error', () => {
            newLink.remove();
            themeSwapRef.current = null;
        }, { once: true });

        themeSwapRef.current = { newLink };
        document.head.appendChild(newLink);
    }, []);

    const toggleTheme = useCallback(() => {
        const newTheme = theme.palette.mode === 'light' ? 'dark' : 'light';
        localStorage.setItem(LS_THEME_KEY, newTheme);

        // Persist to backend (optimistic — localStorage already updated above)
        if (checkFeatureFlag('ui_kanso_root_theme_provider')) {
            sumoFetch('/json/v1/userSettings/update', {
                method: 'POST',
                headers: { 'content-type': 'application/json' },
                body: JSON.stringify({
                    userSettingsMap: { useDarkTheme: newTheme === 'dark' }
                })
            }).catch(error => {
                if (APP_INFO.debug) {
                    console.error('Failed to update theme preference:', error);
                }
            });
        }

        applyTheme(newTheme);
    }, [applyTheme, theme]);

    // Fetch backend theme preference and sync to localStorage if they differ
    const syncThemeFromBackend = () => {
        sumoFetch('/json/v1/userSettings/get', {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({ userSettingsList: ['useDarkTheme'] })
        })
            .then(response => response.json())
            .then(data => {
                const userSettings = data?.userSettingsMap;
                if (!userSettings) return;

                const useDarkTheme = JSON.parse(
                    userSettings.useDarkTheme ?? 'null'
                );
                if (useDarkTheme === null || useDarkTheme === undefined) return;

                const backendTheme = useDarkTheme ? 'dark' : 'light';
                const storedTheme = localStorage.getItem(LS_THEME_KEY);

                if (storedTheme !== backendTheme) {
                    localStorage.setItem(LS_THEME_KEY, backendTheme);
                    applyTheme(backendTheme);
                }
            })
            .catch(error => {
                if (APP_INFO.debug) {
                    console.error('Failed to fetch theme preference:', error);
                }
            });
    };

    useEffect(() => {
        // Preload the opposite theme CSS for fast toggling
        const currentTheme = theme.palette.mode;
        const preloadHref =
            currentTheme === 'light'
                ? SETTINGS.dark_theme_path
                : SETTINGS.light_theme_path;
        const preload = document.createElement('link');
        preload.rel = 'preload';
        preload.as = 'style';
        preload.href = preloadHref;
        document.head.appendChild(preload);

        // Sync theme preference from backend (skip on no-auth pages to avoid 401)
        if (!isPageWithoutLogin && checkFeatureFlag('ui_kanso_root_theme_provider')) {
            syncThemeFromBackend();
        }
    }, []);

    useEffect(() => {
        const handleStorageChange = e => {
            if (e.key === LS_THEME_KEY) {
                const newTheme = e.newValue === 'light' ? 'light' : 'dark';
                applyTheme(newTheme);
            }
        };
        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, [applyTheme]);

    return (
        <IntlProvider locale={langCode} messages={localizedMessages}>
            <ErrorBoundary>
                <ThemeProvider theme={theme}>
                    <AppContext.Provider value={{ ref, toggleTheme }}>
                        <StickyAlertManager id="page-alerts">
                            {isPageWithoutLogin ? (
                                <NoAuthenticationRouter />
                            ) : (
                                <Login>
                                    <CanManager>
                                        <AppRouter />
                                    </CanManager>
                                </Login>
                            )}
                        </StickyAlertManager>
                        {isDevEnv() && <FeatureFlagManager />}
                    </AppContext.Provider>
                </ThemeProvider>
            </ErrorBoundary>
        </IntlProvider>
    );
};

export default App;
